import { invoicesAPI, customersAPI, companiesAPI, customerPaymentsAPI } from '../lib/api';
import { getCurrentCompanyId, getCurrentUser, getAuthToken } from './auth';

/**
 * Test API connectivity and authentication
 */
export const testApiConnection = async () => {
  console.log('🔍 Testing API Connection...');
  
  // Check authentication
  const user = getCurrentUser();
  const token = getAuthToken();
  const companyId = getCurrentCompanyId();
  
  console.log('Auth Status:', {
    hasUser: !!user,
    hasToken: !!token,
    companyId: companyId,
    user: user
  });
  
  if (!user || !token) {
    console.error('❌ Not authenticated');
    return { success: false, error: 'Not authenticated' };
  }
  
  if (!companyId) {
    console.error('❌ No company ID found');
    return { success: false, error: 'No company ID found' };
  }
  
  try {
    // Test companies API first
    console.log('🔍 Testing companies API...');
    const companiesResponse = await companiesAPI.getAll();
    console.log('✅ Companies API response:', companiesResponse.data);
    
    // Test customers API
    console.log('🔍 Testing customers API...');
    const customersResponse = await customersAPI.getAll(companyId);
    console.log('✅ Customers API response:', customersResponse.data);
    
    // Test invoices API
    console.log('🔍 Testing invoices API...');
    const invoicesResponse = await invoicesAPI.getAll(companyId);
    console.log('✅ Invoices API response:', invoicesResponse.data);
    
    // Test customer payments API
    console.log('🔍 Testing customer payments API...');
    const paymentsResponse = await customerPaymentsAPI.getAll(companyId);
    console.log('✅ Customer Payments API response:', paymentsResponse.data);
    
    return {
      success: true,
      data: {
        companies: companiesResponse.data,
        customers: customersResponse.data,
        invoices: invoicesResponse.data,
        payments: paymentsResponse.data
      }
    };
    
  } catch (error) {
    console.error('❌ API Test failed:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });
    
    return {
      success: false,
      error: error.message,
      details: {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      }
    };
  }
};

/**
 * Test specific invoice API endpoint
 */
export const testInvoicesAPI = async (companyId) => {
  console.log('🔍 Testing Invoices API specifically...');
  console.log('Company ID:', companyId);
  
  if (!companyId) {
    console.error('❌ No company ID provided');
    return { success: false, error: 'No company ID provided' };
  }
  
  try {
    const response = await invoicesAPI.getAll(companyId);
    console.log('✅ Invoices API test successful');
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('Response data:', response.data);
    
    return {
      success: true,
      status: response.status,
      data: response.data
    };
    
  } catch (error) {
    console.error('❌ Invoices API test failed:', error);
    
    return {
      success: false,
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    };
  }
};

/**
 * Test customer payments API endpoint
 */
export const testCustomerPaymentsAPI = async (companyId, customerId = null) => {
  console.log('🔍 Testing Customer Payments API...');
  console.log('Company ID:', companyId);
  console.log('Customer ID:', customerId);
  
  if (!companyId) {
    console.error('❌ No company ID provided');
    return { success: false, error: 'No company ID provided' };
  }
  
  try {
    let response;
    
    if (customerId) {
      // Test getting payments for specific customer
      response = await customerPaymentsAPI.getByCustomer(companyId, customerId);
      console.log('✅ Customer-specific payments API test successful');
    } else {
      // Test getting all payments for company
      response = await customerPaymentsAPI.getAll(companyId);
      console.log('✅ All customer payments API test successful');
    }
    
    console.log('Response status:', response.status);
    console.log('Response data:', response.data);
    
    return {
      success: true,
      status: response.status,
      data: response.data
    };
    
  } catch (error) {
    console.error('❌ Customer Payments API test failed:', error);
    
    return {
      success: false,
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    };
  }
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  window.testApiConnection = testApiConnection;
  window.testInvoicesAPI = testInvoicesAPI;
  window.testCustomerPaymentsAPI = testCustomerPaymentsAPI;
}